import React, { useState, useEffect, useRef } from "react";
import { TRYON_SERVER_URL } from "../config";
import { useDispatch, useSelector } from "react-redux";
import { selectAccessToken, selectAuthenticated, selectUserData } from "../selectors";
import { setUserData } from "../store/AuthActions";
import { fetchUser } from "../Utils/UserUtils";
import { createFashionModel, modelCreateCredits } from "../Utils/ModelUtils";
import ToastErrorMessage from "../components/ToastErrorMessage";
import ToastSuccessMessage from "../components/ToastSuccessMessage";
import { useNavigate } from "react-router-dom";
import { uploadExperimentImage } from "../Utils/ExperimentImageUtils";

import Joyride from "react-joyride";
import { useJoyrideStyles } from "../theme";

import FashionPromptBuilder from "../fashion-prompt-builder/FashionPromptBuilder";
import { DownloadIcon, ImageDownIcon, XIcon } from "lucide-react";
import { TOURS, TOUR_PROPS, useTour } from "../Utils/tours";

export default function GenerationModel({
  selectedPrompt,
  setShowPromptGalleryModal,
  showPromptGalleryModal,
}) {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageFile, setUploadedImageFile] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const hiddenFileInputRef = useRef(null);

  const [generatedImageUrl, setGeneratedImageUrl] = useState("");

  const defaultImageSettings = {
    prompt: selectedPrompt ? selectedPrompt : "",
  };
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [generation, setGeneration] = useState(null); // Store the generation object

  const [localUrl, setLocalUrl] = useState(null);

  useEffect(() => {
    if (!generatedImageUrl) return;

    let mounted = true;

    fetchProtectedImage(generatedImageUrl)
      .then((url) => {
        if (mounted) setLocalUrl(url);
      })
      .catch(() => {
        if (mounted) setLocalUrl(generatedImageUrl);
      })
      .finally(() => {
        if (mounted) setIsGenerating(false);
      });

    return () => {
      mounted = false;
    };
  }, [generatedImageUrl]);

  const [showFashionPromptBuilder, setShowFashionPromptBuilder] =
    useState(false);

  const showFashionPromptBuilderModalRef = useRef(null);

  const [imageSettings, setImageSettings] = useState(defaultImageSettings);
  const accessToken = useSelector(selectAccessToken) || null;
  const authenticated = useSelector(selectAuthenticated);
  const userData = JSON.parse(useSelector(selectUserData) || "null");
  const modelCredits = modelCreateCredits(Boolean(uploadedImageFile));
  const [errorMessage, setErrorMessage] = useState("");
  const [errorStatus, setErrorStatus] = useState("");
  const [showToastErrorMessage, setShowToastErrorMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showToastSuccessMessage, setShowToastSuccessMessage] = useState(false);
  const [selectedExperiment, setSelectedExperiment] = useState(null);

  const [isGenerating, setIsGenerating] = useState(false);

  // 1. State for image modal
  const [modalData, setModalData] = useState({ open: false, experiment: null });
  
  // State for authentication modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // State for generated image view modal
  const [showGeneratedImageModal, setShowGeneratedImageModal] = useState(false);

  useEffect(() => {
    console.log("selectedPrompt:", selectedPrompt);
    if (selectedPrompt) {
      handleSettingChange("prompt", selectedPrompt);
    }
  }, [selectedPrompt]);

  useEffect(() => {
    if (!showFashionPromptBuilder) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") setShowFashionPromptBuilder(false);
    };

    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [showFashionPromptBuilder]);

  const handleSettingChange = (setting, value) => {
    setImageSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  const handleFileSelected = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        const imageUrl = URL.createObjectURL(file);
        setUploadedImage(imageUrl);
        setUploadedImageFile(file);
      } else {
        setErrorMessage("Please select a valid image file");
        setErrorStatus("Error");
        setShowToastErrorMessage(true);
      }
    }
    // Reset input value to allow re-uploading the same file
    if (hiddenFileInputRef.current) {
      hiddenFileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = () => {
    if (uploadedImage) {
      URL.revokeObjectURL(uploadedImage);
    }
    setUploadedImage(null);
    setUploadedImageFile(null);
  };

  const getSizeValues = (exp) => {
    if (!exp?.params?.size) return { width: null, height: null };
    const [w, h] = exp.params.size.split("x").map(Number);
    return { width: w, height: h };
  };

  const getAspectRatio = (exp) => {
    return exp?.params?.aspect_ratio || null;
  };

  const handleUploadClick = () => {
    hiddenFileInputRef.current?.click();
  };

  const handleGenerate = async () => {
    if (!authenticated) {
      navigate("/signin");
      return;
    }

    if (!String(imageSettings.prompt || "").trim()) {
      setErrorMessage("Enter a prompt to create a model");
      setErrorStatus("Error");
      setShowToastErrorMessage(true);
      return;
    }

    const balance = Number(userData?.credits);
    if (!Number.isFinite(balance) || balance < modelCredits) {
      navigate(`/credits?buy_credits=true&num_of_credits=${modelCredits}`);
      return;
    }

    setIsGenerating(true);
    setShowToastErrorMessage(false);
    setErrorMessage("");
    setErrorStatus("");

    try {
      const result = await createFashionModel({
        prompt: imageSettings.prompt,
        faceFile: uploadedImageFile,
        accessToken,
      });

      if (result.status !== "success" || !result.data?.result?.image_url) {
        setErrorMessage(result.message || "Failed to create the model");
        setErrorStatus(result.statusCode || "Error");
        setShowToastErrorMessage(true);
        setIsGenerating(false);
        return;
      }

      const imagePath = result.data.result.image_url;
      const imageUrl = imagePath.startsWith("http")
        ? imagePath
        : `${TRYON_SERVER_URL}/${imagePath}`;

      setGeneration(result.data);
      setSelectedExperiment(result.data);
      setGeneratedImageUrl(imageUrl);
      handleSettingChange("prompt", "");

      const user = await fetchUser(accessToken);
      if (user.status === "success") {
        dispatch(setUserData(JSON.stringify(user.data)));
      }
    } catch (err) {
      console.error("Error creating model:", err);
      setErrorMessage(err?.message || "Failed to create the model");
      setErrorStatus("Error");
      setShowToastErrorMessage(true);
      setIsGenerating(false);
    }
  };

  const steps = [
    {
      target: ".enter_prompt",
      content:
        "Enter a prompt to generate an image. Please describe what you want to see in detail to get the best results.",
      disableBeacon: true,
    },
    {
      target: ".select_build_prompt",
      content:
        "Select a prompt from the prompt gallery, or use the prompt builder to generate a prompt.",
      disableBeacon: true,
    },
    {
      target: ".reference_image",
      content:
        "Add a face photo if you want the model to follow that face. This is optional.",
      disableBeacon: true,
    },
    {
      target: ".generate_button",
      content:
        "Click the generate button to generate an image. The generation will start in a few seconds.",
      disableBeacon: true,
    },
    {
      target: ".generated_images",
      content:
        "See the generated images. You can click on the image to see the full size.",
      disableBeacon: true,
    }
  ];

  const tourStyles = useJoyrideStyles();
  const { done: tourDone, onCallback: handleJoyrideCallback } = useTour(TOURS.MODEL_GENERATOR);

  const handleErrorClose = () => {
    setShowToastErrorMessage(false);
    setErrorMessage("");
    setErrorStatus("");
  };

  // downloading images
  const handleDownload = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "generated_image.jpg";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 200);
    } catch (e) {
      alert("Failed to download image");
    }
  };

  // Save to gallery
  const handleSaveToGallery = async () => {
    try {
      if (!localUrl) {
        setShowToastErrorMessage(true);
        setErrorMessage("No image available to save to your gallery");
        setErrorStatus("Missing Image");
        return;
      }

      if (!authenticated) {
        navigate("/signin");
        return;
      }

      // Convert localUrl to file
      const response = await fetch(localUrl);
      const blob = await response.blob();
      // Create a File object with appropriate name and type
      const fileName = `image_${Date.now()}.png`;
      const file = new File([blob], fileName, { type: "image/png" });
      const result = await uploadExperimentImage(file, accessToken, {});
      
      if (result.status === "success") {
        setShowToastSuccessMessage(true);
        setSuccessMessage("Image successfully added to your gallery");
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setShowToastSuccessMessage(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        setShowToastErrorMessage(true);
        setErrorMessage(
          "Unable to save image to your gallery. Please try again."
        );
        setErrorStatus("Save Failed");
      }
    } catch (error) {
      console.error("Error saving to gallery:", error);
      setShowToastErrorMessage(true);
      setErrorMessage(
        "Connection issue while saving your image. Please check your internet connection and try again."
      );
      setErrorStatus("Connection Error");
    }
  };

  const handleSuccessClose = () => {
    setShowToastSuccessMessage(false);
    setSuccessMessage("");
  };

  //  prompt robustly
  const getPrompt = (exp) => {
    let prompt = exp?.result?.prompt || exp?.prompt;
    if (!prompt && typeof exp?.params === "object") {
      prompt = exp.params.prompt;
    }
    if (!prompt && typeof exp?.params === "string") {
      try {
        const pObj = JSON.parse(exp.params);
        prompt = pObj.prompt;
      } catch {}
    }
    return prompt || "No prompt";
  };

  async function fetchProtectedImage(imageUrl) {
    const response = await fetch(imageUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch image");
    }

    const blob = await response.blob();
    const localUrl = URL.createObjectURL(blob);
    return localUrl;
  }

  const handleLoginRedirect = () => {
    navigate("/signin");
  };

  return (
    <>
      {!tourDone ? (
        <Joyride {...TOUR_PROPS} callback={handleJoyrideCallback} steps={steps} styles={tourStyles} />
      ) : null}
     
        <div className="grid h-[calc(100vh-5rem)] w-full grid-cols-1 gap-4 overflow-y-auto bg-ground p-4 selection:bg-primary-100 lg:grid-cols-12 lg:overflow-hidden lg:min-h-0">
          <div className="flex min-h-[28rem] flex-col rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] lg:col-span-5 lg:h-full lg:min-h-0 lg:overflow-hidden">
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                    <div className="enter_prompt">
                      <label className="mb-2 block font-Mulish font-semibold text-lg text-secondary">Prompt</label>

                      <textarea
                        placeholder="Describe the model. Include age, hair, and expression."
                        value={imageSettings.prompt}
                        onChange={(e) =>
                          handleSettingChange("prompt", e.target.value)
                        }
                        className="h-24 w-full resize-none rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
                      />

                      <div className="select_build_prompt mt-3 flex flex-col gap-2 sm:flex-row">
                        <button
                          onClick={() => {
                            showPromptGalleryModal("model");
                          }}
                          className="browse_prompt_gallery inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
                            />
                          </svg>
                          <span className="truncate">Prompt Gallery</span>
                        </button>

                        {/* Prompt Builder */}
                        <button
                          onClick={() => {
                            setShowFashionPromptBuilder(true);
                          }}
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16.862 4.487a2.25 2.25 0 013.182 3.182l-8.625 8.625a4.5 4.5 0 01-1.591 1.01l-3.005 1.001a.75.75 0 01-.948-.948l1.002-3.005a4.5 4.5 0 011.01-1.591l8.625-8.625z"
                            />
                          </svg>
                          <span className="truncate">Prompt Builder</span>
                        </button>
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="reference_image">
                      <h2 className="mb-2 font-Mulish font-semibold text-lg text-secondary">
                        Face photo
                      </h2>
                      <p className="mb-3 font-Mulish text-sm text-graphite">The model follows this face.</p>
                      {uploadedImage ? (
                        <div className="relative rounded-2xl bg-primary-50 p-2">
                          <div
                            onClick={() => setShowImagePreview(true)}
                            className="flex h-32 cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-white"
                          >
                            <img
                              src={uploadedImage}
                              alt="Face photo"
                              className="max-h-full max-w-full object-contain"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage();
                            }}
                            className="absolute right-4 top-4 rounded-full border border-hairline bg-white p-2"
                            aria-label="Remove photo"
                            title="Remove image"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 text-primary-700"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleUploadClick}
                          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-50 px-4 py-8 font-Mulish text-sm text-graphite hover:bg-primary-100"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-primary-700"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          Upload a face photo
                        </button>
                      )}
                      <input
                        ref={hiddenFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelected}
                        className="hidden"
                      />
                    </div>
            </div>

            <button
              type="button"
              className={`generate_button mt-4 w-full shrink-0 rounded-full px-4 py-2.5 font-Mulish font-semibold text-sm ${isGenerating ? "cursor-not-allowed bg-primary-100 text-primary-800" : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"}`}
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? "Creating..." : `Generate (${modelCredits} credits)`}
            </button>
          </div>

          <div className="generated_images flex min-h-[20rem] flex-col rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] lg:col-span-7 lg:h-full lg:min-h-0 lg:overflow-hidden">
            <div className="mb-3 flex shrink-0 items-center justify-between">
              <h2 className="font-Mulish font-semibold text-lg text-secondary">Result</h2>
              {selectedExperiment && selectedExperiment.status === "completed" ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleSaveToGallery()}
                    className="rounded-full border border-hairline bg-white p-2 text-primary-700 hover:bg-primary-50"
                    aria-label="Save to gallery"
                    title="Save to Gallery"
                  >
                    <ImageDownIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDownload(generatedImageUrl)}
                    className="rounded-full border border-hairline bg-white p-2 text-primary-700 hover:bg-primary-50"
                    aria-label="Download image"
                    title="Download Image"
                  >
                    <DownloadIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : null}
            </div>

        {authenticated === null ? (
          <div className="min-h-[20rem] flex-1 animate-pulse rounded-2xl bg-primary-50" />
        ) : authenticated ? (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
              {isGenerating ? (
                <div className="flex min-h-[16rem] flex-1 flex-col items-center justify-center rounded-2xl bg-primary-50 p-4 text-center">
                  <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-primary-200 border-t-primary-700" />
                  <p className="mt-3 font-Mulish font-semibold text-secondary">Creating the model</p>
                  <p className="mt-1 font-Mulish text-sm text-graphite">This might take a few moments</p>
                </div>
              ) : selectedExperiment ? (
                selectedExperiment.status === "completed" ? (
                  <div className="flex min-h-0 flex-1 flex-col gap-4">
                    <div className="flex min-h-[16rem] flex-1 items-center justify-center overflow-hidden rounded-2xl bg-primary-50 p-4">
                      <img
                        src={localUrl}
                        alt="Generated fashion model"
                        onClick={() => setShowGeneratedImageModal(true)}
                        className="max-h-full max-w-full cursor-pointer rounded-lg object-contain"
                      />
                    </div>

                    <div>
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <span className="rounded-full bg-primary-50 px-3 py-1.5 font-Mulish text-sm text-primary-700">
                          Completed
                        </span>
                        <span className="font-Mulish text-sm text-graphite">
                          {new Date(selectedExperiment.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="mb-2 font-Mulish text-sm text-graphite">Prompt</p>
                      <div className="rounded-lg border border-hairline bg-white p-3">
                        <p className="line-clamp-3 font-Mulish text-sm text-secondary">
                          {getPrompt(selectedExperiment)}
                        </p>
                      </div>
                    </div>

                      {/* HEIGHT / WIDTH / ASPECT */}
                      {(() => {
                        const { width, height } =
                          getSizeValues(selectedExperiment);
                        const aspectRatio = getAspectRatio(selectedExperiment);

                        return (
                          <div className="grid grid-cols-2 gap-2 sm:gap-3">
                            {width && (
                              <div className="rounded-lg border border-hairline bg-white p-3">
                                <p className="font-Mulish text-sm text-graphite">Width</p>
                                <p className="font-Mulish font-semibold text-sm text-secondary">{width}px</p>
                              </div>
                            )}
                            {height && (
                              <div className="rounded-lg border border-hairline bg-white p-3">
                                <p className="font-Mulish text-sm text-graphite">Height</p>
                                <p className="font-Mulish font-semibold text-sm text-secondary">{height}px</p>
                              </div>
                            )}
                            {aspectRatio && (
                              <div className="col-span-2 rounded-lg border border-hairline bg-white p-3">
                                <p className="font-Mulish text-sm text-graphite">Aspect ratio</p>
                                <p className="font-Mulish font-semibold text-sm text-secondary">{aspectRatio}</p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                  </div>
                ) : selectedExperiment.status === "failed" ? (
                  <div className="flex min-h-[16rem] flex-1 flex-col items-center justify-center rounded-2xl bg-primary-50 p-6 text-center">
                    <p className="font-Mulish font-semibold text-lg text-secondary">The model was not created</p>
                    <p className="mt-1 font-Mulish text-sm text-graphite">Try again with a different prompt.</p>
                  </div>
                ) : null
              ) : (
                <div className="flex min-h-[16rem] flex-1 flex-col items-center justify-center rounded-2xl bg-primary-50 p-6 text-center">
                  <div className="mx-auto h-10 w-10 text-primary-700">
                    <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="mt-2 font-Mulish font-semibold text-lg text-secondary">No model yet</p>
                  <p className="mt-1 font-Mulish text-sm text-graphite">Your model will show up here</p>
                </div>
              )}
            </div>
            ) : (
              <div className="flex min-h-[16rem] flex-1 flex-col items-center justify-center rounded-2xl bg-primary-50 p-6 text-center">
                <div className="mx-auto h-10 w-10 text-primary-700">
                  <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="mt-2 font-Mulish font-semibold text-lg text-secondary">No model yet</p>
                <p className="mt-1 font-Mulish text-sm text-graphite">Your model will show up here</p>
              </div>
            )}
        </div>

        {/* Modal*/}
        {modalData.open && modalData.experiment && (
          <>
            <div
              className="fixed inset-0 z-[2000] bg-black/40"
              onClick={() => setModalData({ open: false, experiment: null })}
            />
            <div className="fixed inset-0 flex items-center justify-center z-[2001] p-4">
              <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)] md:flex-row">
                <div className="flex flex-1 items-center justify-center bg-primary-50 p-6">
                  <img
                    src={`${TRYON_SERVER_URL}/${modalData.experiment.result.image_url}`}
                    alt="Full Preview"
                    className="h-auto max-h-[76vh] w-auto max-w-full rounded-lg bg-white"
                  />
                </div>

                <div className="md:w-80 w-full md:border-r border-gray-200 bg-gray-50 px-7 py-7 flex-shrink-0 flex flex-col gap-8 overflow-y-auto max-h-[84vh]">
                  <div>
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-gray-800 mb-2">
                        Prompt
                      </h3>
                      <div className="bg-white rounded-xl py-3 px-4 shadow border text-base text-gray-700 font-medium whitespace-pre-line break-words">
                        {getPrompt(modalData.experiment)}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-md font-semibold text-gray-700 mb-2">
                        Settings
                      </h4>
                      <div className="divide-y divide-gray-100">
                        {Object.entries(modalData.experiment || {}).map(
                          ([key, val]) => {
                            if (typeof val === "object" || key === "result")
                              return null;
                            return (
                              <div
                                key={key}
                                className="flex justify-between items-center py-1 text-sm text-gray-700"
                              >
                                <span className="font-medium capitalize">
                                  {key}:
                                </span>
                                <span>{String(val)}</span>
                              </div>
                            );
                          }
                        )}
                        {modalData.experiment?.result &&
                          Object.entries(modalData.experiment.result).map(
                            ([key, val]) => {
                              if (["image_url", "prompt"].includes(key))
                                return null;
                              return (
                                <div
                                  key={key}
                                  className="flex justify-between items-center py-1 text-sm text-gray-700"
                                >
                                  <span className="font-medium capitalize">
                                    {key}:
                                  </span>
                                  <span>{String(val)}</span>
                                </div>
                              );
                            }
                          )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* CLOSE BUTTON */}
                <button
                  className="absolute right-3 top-3 z-[2010] rounded-full border border-hairline bg-white p-2 text-primary-700"
                  onClick={() =>
                    setModalData({ open: false, experiment: null })
                  }
                  aria-label="Close preview"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}

        {/* Image Preview Modal */}
        {showImagePreview && uploadedImage && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center p-2 sm:p-4">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowImagePreview(false)}
            />
            <div className="relative z-[2001] max-w-4xl w-full max-h-[90vh]">
              <div className="overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
                <div className="flex items-center justify-center bg-primary-50 p-4">
                  <img
                    src={uploadedImage}
                    alt="Full preview"
                    className="max-w-full max-h-[75vh] sm:max-h-[80vh] object-contain rounded-lg"
                  />
                </div>
                <button
                  onClick={() => setShowImagePreview(false)}
                  className="absolute right-3 top-3 rounded-full border border-hairline bg-white p-2 text-primary-700"
                  aria-label="Close preview"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Generated Image View Modal */}
        {showGeneratedImageModal && localUrl && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center p-2 sm:p-4">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowGeneratedImageModal(false)}
            />
            <div className="relative z-[2001] max-w-6xl w-full max-h-[95vh]">
              <div className="overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
                <div className="flex min-h-[50vh] items-center justify-center bg-primary-50 p-6">
                  <img
                    src={localUrl}
                    alt="Generated fashion model"
                    className="h-auto max-h-[80vh] w-auto max-w-full rounded-lg object-contain"
                  />
                </div>
                {/* Action Buttons */}
                <div className="absolute top-2 right-2 flex items-center gap-1.5 sm:gap-2 z-10">
                  {/* Save to Gallery Button */}
                  <button
                    onClick={() => handleSaveToGallery()}
                    className="rounded-full border border-hairline bg-white p-2 text-primary-700"
                    aria-label="Save to gallery"
                    title="Save to Gallery"
                  >
                    <ImageDownIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
                  </button>
                  {/* Download Button */}
                  <button
                    onClick={() => handleDownload(generatedImageUrl)}
                    className="rounded-full border border-hairline bg-white p-2 text-primary-700"
                    aria-label="Download image"
                    title="Download Image"
                  >
                    <DownloadIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
                  </button>
                  {/* Close Button */}
                  <button
                    onClick={() => setShowGeneratedImageModal(false)}
                    className="rounded-full border border-hairline bg-white p-2 text-primary-700"
                    aria-label="Close preview"
                  >
                    <XIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showToastErrorMessage && (
          <ToastErrorMessage
            errorMessage={errorMessage}
            errorStatus={errorStatus}
            handleErrorClose={handleErrorClose}
          />
        )}
        {showToastSuccessMessage && (
          <ToastSuccessMessage
            message={successMessage}
            handleClose={handleSuccessClose}
          />
        )}
        {/* //{showGenerateFashionModelPromptBuilder */}
        {showFashionPromptBuilder && (
          <>
            {/* <GenerateFashionModelPromptBuilder
                isModal={true}
                setShowGenerateFashionModelPromptBuilderModal={
                  setShowGenerateFashionModelPromptBuilder
                }
                onSelectPrompt={(prompt) =>
                  handleSettingChange("prompt", prompt)
                }
                showGenerateFashionModelPromptBuilderModalRef={
                  showGenerateFashionModelPromptBuilderModalRef
                }
              /> */}
            <FashionPromptBuilder
              isModal={true}
              setShowFashionPromptBuilder={setShowFashionPromptBuilder}
              onSelectPrompt={(prompt) => handleSettingChange("prompt", prompt)}
              showFashionPromptBuilderRef={showFashionPromptBuilderModalRef}
            />
          </>
        )}

        {/* Authentication Modal */}
        {showAuthModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
            <div className="relative w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-[0_0_0_1px_var(--hairline)]">
              <button
                type="button"
                onClick={() => setShowAuthModal(false)}
                className="absolute right-3 top-3 rounded-full border border-hairline bg-white p-2 text-primary-700"
                aria-label="Close modal"
              >
                <XIcon className="h-4 w-4" />
              </button>
              <p className="font-Mulish font-semibold text-lg text-secondary">Sign in to create a model</p>
              <p className="mt-1 font-Mulish text-sm text-graphite">Sign in to create a fashion model from a prompt.</p>
              <button
                type="button"
                onClick={handleLoginRedirect}
                className="mt-4 rounded-full bg-primary-700 px-6 py-2.5 font-Mulish font-semibold text-sm text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
              >
                Sign in
              </button>
            </div>
          </div>
        )}
        </div>
     
    </>
  );
}
